import { useContext, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { PersonRelationType } from '@dealstack/shared/lib/generated';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import {
	addRelatedPersonByEmail,
	lookupNaturalPersonByEmail,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/apis/relatedPersons.ts';
import { AddRelatedPersonFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/AddRelatedPersonDrawer/addRelatedPersonShape.ts';
import {
	ADD_RELATED_PERSON_FORM_FIELDS,
	AddRelatedPersonFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPersonsSection/AddRelatedPersonDrawer/addRelatedPersonDrawer.types.ts';

export const useAddRelatedPersonDrawer = ({
	opened,
	onClose,
	onSuccess,
}: {
	opened: boolean;
	onClose: () => void;
	onSuccess: () => void;
}) => {
	const { selectedEntityId, refetchPersons } = useContext(CapManageControllerContext);

	const [matchedExisting, setMatchedExisting] = useState(false);

	const form = useForm<AddRelatedPersonFormValues>({
		mode: 'all',
		resolver: zodResolver(AddRelatedPersonFormShape),
	});

	const email = useWatch({ control: form.control, name: ADD_RELATED_PERSON_FORM_FIELDS.email });
	const firstName = useWatch({
		control: form.control,
		name: ADD_RELATED_PERSON_FORM_FIELDS.first_name,
	});
	const lastName = useWatch({
		control: form.control,
		name: ADD_RELATED_PERSON_FORM_FIELDS.last_name,
	});

	const lookupMutation = useMutation({ mutationFn: lookupNaturalPersonByEmail });
	const addMutation = useMutation({ mutationFn: addRelatedPersonByEmail });

	useEffect(() => {
		if (!opened) {
			form.reset({});
			setMatchedExisting(false);
			lookupMutation.reset();
		}
	}, [opened]);

	useEffect(() => {
		if (matchedExisting) {
			setMatchedExisting(false);
			form.setValue(ADD_RELATED_PERSON_FORM_FIELDS.first_name, '');
			form.setValue(ADD_RELATED_PERSON_FORM_FIELDS.last_name, '');
		}
	}, [email]);

	const onLookup = async () => {
		const isEmailValid = await form.trigger(ADD_RELATED_PERSON_FORM_FIELDS.email);
		if (!isEmailValid || !selectedEntityId) {
			return;
		}

		lookupMutation.mutate(
			{ email },
			{
				onSuccess: (match) => {
					const found = !!match?.found;
					setMatchedExisting(found);
					if (found) {
						form.setValue(
							ADD_RELATED_PERSON_FORM_FIELDS.first_name,
							match?.firstName ?? ''
						);
						form.setValue(
							ADD_RELATED_PERSON_FORM_FIELDS.last_name,
							match?.lastName ?? ''
						);
					}
				},
			}
		);
	};

	const onSubmit = async (values: AddRelatedPersonFormValues) => {
		if (!selectedEntityId) {
			return;
		}

		await addMutation.mutateAsync(
			{
				personId: selectedEntityId,
				body: {
					email: values.email,
					relationType: values.relation_type,
					...(values.relation_type === PersonRelationType.OTHER
						? { customRelationType: values.custom_relation_type?.trim() }
						: {}),
					...(matchedExisting
						? {}
						: { firstName: values.first_name, lastName: values.last_name }),
				},
			},
			{
				onSuccess: () => {
					onSuccess();
					void refetchPersons();
					onClose();
				},
			}
		);
	};

	const namesProvided = !!firstName?.trim() && !!lastName?.trim();
	const canSubmit =
		form.formState.isValid && (matchedExisting || namesProvided) && !addMutation.isPending;

	return {
		form,
		onLookup,
		onSubmit,
		matchedExisting,
		isLookingUp: lookupMutation.isPending,
		isSubmitting: addMutation.isPending,
		canSubmit,
	};
};
